/**
 * Shared repository helpers for the four OCC-managed tabs.
 *
 * Each tab gets `list`, `get`, `create`, `update`, `softDelete`. All reads
 * filter `deleted_at` non-empty unless `{ includeDeleted: true }` is passed.
 *
 * `update` / `softDelete` flow through `genericUpdate` / `genericSoftDelete`
 * helpers below, which themselves call the object-arg `updateRow` /
 * `softDeleteRow` in `occ.ts`. Doing the conversion at the helper boundary
 * keeps every per-tab repo's signature stable while the OCC layer enforces
 * named arguments at the dangerous edge.
 */

import type { SheetsClient } from '../client';
import { TABS, tabRange } from '../schema';
import type { TabName } from '../schema';
import { nowIso, objectToRow, rowToObject } from '../row-utils';
import { softDeleteRow, updateRow } from '../occ';
import type { OccTab } from '../occ';

export interface ListOptions {
  includeDeleted?: boolean;
}

export interface RepoBase<T> {
  list(
    client: SheetsClient,
    spreadsheetId: string,
    options?: ListOptions,
  ): Promise<T[]>;
  get(
    client: SheetsClient,
    spreadsheetId: string,
    id: string,
  ): Promise<T | null>;
  create(
    client: SheetsClient,
    spreadsheetId: string,
    input: Partial<T> & { id?: string },
  ): Promise<T>;
  update(
    client: SheetsClient,
    spreadsheetId: string,
    id: string,
    patch: Partial<T>,
    expectedRev: number,
  ): Promise<{ rev: number; updated_at: string }>;
  softDelete(
    client: SheetsClient,
    spreadsheetId: string,
    id: string,
    expectedRev: number,
  ): Promise<{ rev: number; deleted_at: string }>;
}

export function genId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `r-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export function patchToCells(patch: Record<string, unknown>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(patch)) {
    if (v == null) {
      out[k] = '';
    } else if (typeof v === 'boolean') {
      out[k] = v ? 'true' : 'false';
    } else {
      out[k] = String(v);
    }
  }
  return out;
}

export async function readAllRows(
  client: SheetsClient,
  spreadsheetId: string,
  tab: TabName,
  options: ListOptions = {},
): Promise<Array<Record<string, string>>> {
  const headers = TABS[tab].headers;
  const range = tabRange(tab, { startRow: 2 });
  const resp = await client.valuesGet(spreadsheetId, range);
  const rows = (resp.values ?? []) as string[][];
  const objs = rows.filter((r) => (r[0] ?? '') !== '').map((r) => rowToObject(headers, r));
  const filtered = options.includeDeleted ? objs : objs.filter((o) => !o['deleted_at']);
  // Diagnostic — surfaces in browser console so we can see what the Sheet
  // actually returned on cold load. Helps catch stale-cache / wrong-sheet
  // / soft-delete-misfilter footguns.
  // eslint-disable-next-line no-console
  console.info(`[Mochia/sheets] readAllRows(${tab})`, {
    spreadsheetId: spreadsheetId.slice(0, 8) + '…',
    range,
    rawRowCount: rows.length,
    nonEmptyIdCount: objs.length,
    afterDeletedFilter: filtered.length,
    sampleIds: filtered.slice(0, 3).map((o) => o['id']),
  });
  return filtered;
}

export async function appendRow(
  client: SheetsClient,
  spreadsheetId: string,
  tab: TabName,
  obj: Record<string, string>,
): Promise<void> {
  const headers = TABS[tab].headers;
  const values = objectToRow(headers, obj);
  await client.valuesAppend(spreadsheetId, `${tab}!A1`, [values]);
}

/** Build a freshly stamped row with id/rev/timestamps.
 *
 *  New rows start at `rev=1` — see `proposal.md` "rev=0 ambiguity". The
 *  reserved value `0` means "uninitialised / missing" so the OCC layer can
 *  distinguish a freshly-created row (rev=1) from a corrupt/legacy cell.
 */
export function newRowDefaults(extra: Record<string, unknown> = {}): Record<string, string> {
  const now = nowIso();
  return patchToCells({
    id: genId(),
    rev: 1,
    created_at: now,
    updated_at: now,
    deleted_at: '',
    ...extra,
  });
}

export async function genericUpdate(
  client: SheetsClient,
  spreadsheetId: string,
  tab: OccTab,
  id: string,
  patch: Record<string, unknown>,
  expectedRev: number,
): Promise<{ rev: number; updated_at: string }> {
  return updateRow({
    client,
    spreadsheetId,
    tab,
    id,
    patch: patchToCells(patch),
    expectedRev,
  });
}

export async function genericSoftDelete(
  client: SheetsClient,
  spreadsheetId: string,
  tab: OccTab,
  id: string,
  expectedRev: number,
): Promise<{ rev: number; deleted_at: string }> {
  return softDeleteRow({ client, spreadsheetId, tab, id, expectedRev });
}
